import type { InfiniteData } from '@tanstack/react-query';

/**
 * Realtime cache surgery for keyset-paginated message threads. New/changed
 * messages are merged DIRECTLY into the `useInfiniteQuery` cache instead of
 * invalidating it — invalidating an infinite query refetches every loaded page
 * with its stored cursor, and a fresh tip message shifts the page boundaries,
 * which drops a row into the gap. Merging the newest page in keeps older pages
 * (and the user's scroll position) untouched and gap-free.
 *
 * Shared `THREAD_PAGE_LIMIT` keeps the inbox `useInfiniteQuery` input and the
 * realtime tip-refetch on the SAME query key so `setInfiniteData` targets the
 * right cache entry.
 */
export const THREAD_PAGE_LIMIT = 30;

/**
 * Options EVERY realtime tip refetch must pass to `utils.*.messages.fetch()`.
 *
 * `fetch()` is `queryClient.fetchQuery`, which resolves with the CACHED value
 * when the entry is not stale rather than hitting the network:
 *
 *     query.isStaleByTime(...) ? query.fetch(...) : Promise.resolve(query.state.data)
 *
 * The app sets a global `staleTime: 30_000` (`main.tsx`), and the tip fetch
 * lands on its own `type:'query'` cache key that nothing subscribes to — so
 * without this the second refresh within 30s silently returns the first
 * refresh's page, the merge finds nothing new, and the open thread freezes
 * until the entry goes stale. That was a real production defect: only the
 * first message of a conversation ever appeared.
 */
export const TIP_FETCH_OPTS = { staleTime: 0 } as const;

/** A keyset page as returned by every `*.messages` procedure. */
export interface ThreadPage<T> {
  items: T[];
  nextCursor: number | null;
}

// The page-param type (`P`) is left generic at every helper: `useInfiniteQuery`
// surfaces the cursor as `number | undefined` while `setInfiniteData` types it
// `number | null`, so pinning it here would reject one caller or the other.
// The helpers never touch `pageParams` (only `pages`), so `P` just flows
// through untouched.

/**
 * Fold a freshly-fetched newest page into an infinite thread cache:
 *   • rows already present are replaced in place (delivery-status ticks, edits,
 *     reactions on recently-visible messages),
 *   • genuinely-new tail rows are appended to the newest page (`pages[0]`,
 *     which holds the newest window in ascending order).
 * Older pages are never re-windowed, so no keyset row is ever lost.
 */
export function mergeNewestPage<T extends { id: number }, P>(
  old: InfiniteData<ThreadPage<T>, P> | undefined,
  tip: ThreadPage<T>,
): InfiniteData<ThreadPage<T>, P> | undefined {
  if (!old || old.pages.length === 0) return old;
  const tipById = new Map(tip.items.map((i) => [i.id, i]));
  const seen = new Set<number>();
  const pages = old.pages.map((p) => ({
    ...p,
    items: p.items.map((it) => {
      seen.add(it.id);
      return tipById.get(it.id) ?? it;
    }),
  }));
  const fresh = tip.items.filter((i) => !seen.has(i.id));
  if (fresh.length) {
    const newest = pages[0]!;
    pages[0] = { ...newest, items: [...newest.items, ...fresh] };
  }
  return { ...old, pages };
}

/**
 * Apply ONE pushed message to the thread — the fast path for a realtime
 * broadcast (or a mutation's own response) that already carries the full DTO.
 *
 * Same rule as `mergeNewestPage`, expressed for a single row: replace in place
 * when the id is already loaded (status ticks, edits, reactions), otherwise
 * append to the newest page. Older pages are never re-windowed, so no keyset
 * row is lost, and re-applying the same message is a no-op — which keeps a
 * pushed row and a later reconciliation refetch from double-inserting.
 */
export function upsertMessage<T extends { id: number }, P>(
  old: InfiniteData<ThreadPage<T>, P> | undefined,
  message: T,
): InfiniteData<ThreadPage<T>, P> | undefined {
  return mergeNewestPage(old, { items: [message], nextCursor: null });
}

/**
 * Collapse a burst of realtime events into a single in-flight refetch.
 *
 * Reconciliation only runs for events that carry no usable payload, but those
 * can still arrive in bursts (a campaign echo storm, a handset sending ten
 * messages). Without coalescing each one costs a full page fetch per connected
 * client. `schedule()` starts a run if none is active and otherwise sets a
 * trailing flag, so a burst of N events costs one request — plus one more when
 * events landed while the first was in flight, so nothing is missed.
 */
export function createCoalescer(run: () => Promise<unknown>): { schedule: () => void } {
  let inFlight = false;
  let trailing = false;

  const start = (): void => {
    inFlight = true;
    void run()
      .catch(() => {})
      .finally(() => {
        inFlight = false;
        if (trailing) {
          trailing = false;
          start();
        }
      });
  };

  return {
    schedule: () => {
      if (inFlight) {
        trailing = true;
        return;
      }
      start();
    },
  };
}

/** Drop a message (by id) from every page — for MessageDeleted broadcasts. */
export function removeMessageFromThread<T extends { id: number }, P>(
  old: InfiniteData<ThreadPage<T>, P> | undefined,
  id: number,
): InfiniteData<ThreadPage<T>, P> | undefined {
  if (!old) return old;
  return {
    ...old,
    pages: old.pages.map((p) => ({ ...p, items: p.items.filter((i) => i.id !== id) })),
  };
}

/** Flatten infinite pages (newest-page-first) into one ascending list. */
export function flattenThread<T, P>(data: InfiniteData<ThreadPage<T>, P> | undefined): T[] {
  if (!data) return [];
  // pages are [newest, older, older2…]; reverse so the list reads oldest→newest.
  return data.pages
    .slice()
    .reverse()
    .flatMap((p) => p.items);
}
